<script setup lang="ts">
	import { computed, onMounted, ref } from 'vue'
	import { useI18n } from 'vue-i18n'
	import { toast } from '@/libs/toaster'
	import vSelect from 'vue-select'
	import VModalFinal from '@/components/VModalFinal.vue'
	import { _translations } from './_translations'
	import { useLocationStore } from '@/stores/location.store'
	import { useHubStore } from '@/stores/hubs.store'
	import { useComappStore } from '@/stores/comapps.store'
	import ConfirmButton from '@/components/ConfirmButton.vue'
	import StatusLight from '@/components/StatusLight.vue'

	const props = defineProps<{
		modalId: string
		locationId: string | null
	}>()

	const { t: _tr } = useI18n(_translations)
	const locationStore = useLocationStore()
	const hubStore = useHubStore()
	const comappStore = useComappStore()

	const selectedHubId = ref<string | null>(null)
	const selectedComappId = ref<string | null>(null)
	const isAssigningHub = ref(false)
	const isAssigningComapp = ref(false)

	const locationName = computed(() => {
		const locId = props.locationId || ''
		if (!locId) return ''
		const loc = locationStore.locationsMap[locId]
		return loc?.name || loc?.meta?.name || locId
	})

	const modalTitle = computed(() => {
		const base = _tr('location.assignDevices')
		return locationName.value ? `${base} · ${locationName.value}` : base
	})

	const getLocationLabel = (locId?: string) => {
		if (!locId) return _tr('location.unassigned')
		const loc = locationStore.locationsMap[locId]
		return loc?.name || loc?.meta?.name || locId
	}

	const getAssignmentLabel = (locId?: string) => {
		if (!locId) return _tr('location.unassigned')
		return _tr('location.assignedTo', { name: getLocationLabel(locId) })
	}

	const assignableHubs = computed(() => {
		if (!props.locationId) return []
		return hubStore.hubs
			.filter((hub) => hub.locationId !== props.locationId)
			.map((hub) => ({
				value: hub._id,
				label: hub.name || hub.serial || hub._id,
				locationLabel: getAssignmentLabel(hub.locationId),
			}))
			.sort((a, b) => a.label.localeCompare(b.label))
	})

	const assignableComapps = computed(() => {
		if (!props.locationId) return []
		return comappStore.comapps
			.filter((comapp) => comapp.locationId !== props.locationId)
			.map((comapp) => ({
				value: comapp._id,
				label: comapp.pcName || comapp._id,
				locationLabel: getAssignmentLabel(comapp.locationId),
			}))
			.sort((a, b) => a.label.localeCompare(b.label))
	})

	const assignedHubs = computed(() => {
		if (!props.locationId) return []
		const list = hubStore.hubsByLoc?.[props.locationId] || []
		return list
			.slice()
			.sort((a, b) => (a.name || a.serial || a._id).localeCompare(b.name || b.serial || b._id))
	})

	const assignedComapps = computed(() => {
		if (!props.locationId) return []
		const list = comappStore.comappsByLId?.[props.locationId] || []
		return list
			.slice()
			.sort((a, b) => (a.pcName || a._id).localeCompare(b.pcName || b._id))
	})

	const assignHub = async () => {
		if (!props.locationId || !selectedHubId.value) return
		const hub = hubStore.hubs.find((item) => item._id === selectedHubId.value)
		if (!hub) return
		isAssigningHub.value = true
		try {
			await hubStore.updateHub({ _id: hub._id, locationId: props.locationId } as any)
			await hubStore.listAllHubs()
			selectedHubId.value = null
			toast.success(_tr('location.hubAssigned'))
		} catch (error: any) {
			toast.error(error?.message || error || _tr('location.assignFailed'))
		} finally {
			isAssigningHub.value = false
		}
	}

	const assignComapp = async () => {
		if (!props.locationId || !selectedComappId.value) return
		const comapp = comappStore.comapps.find((item) => item._id === selectedComappId.value)
		if (!comapp) return
		isAssigningComapp.value = true
		try {
			await comappStore.updateComapp({ _id: comapp._id, locationId: props.locationId } as any)
			selectedComappId.value = null
			toast.success(_tr('location.comappAssigned'))
		} catch (error: any) {
			toast.error(error?.message || error || _tr('location.assignFailed'))
		} finally {
			isAssigningComapp.value = false
		}
	}

	const deassignHub = async (hubId: string) => {
		if (!hubId) return
		isAssigningHub.value = true
		try {
			await hubStore.updateHub({ _id: hubId, locationId: '' } as any)
			await hubStore.listAllHubs()
			toast.success(_tr('location.hubUnassigned'))
		} catch (error: any) {
			toast.error(error?.message || error || _tr('location.assignFailed'))
		} finally {
			isAssigningHub.value = false
		}
	}

	const deassignComapp = async (comappId: string) => {
		if (!comappId) return
		isAssigningComapp.value = true
		try {
			await comappStore.updateComapp({ _id: comappId, locationId: '' } as any)
			toast.success(_tr('location.comappUnassigned'))
		} catch (error: any) {
			toast.error(error?.message || error || _tr('location.assignFailed'))
		} finally {
			isAssigningComapp.value = false
		}
	}

	onMounted(async () => {
		try {
			if (!locationStore.locations.length) await locationStore.listLocations()
		} catch (error) {
			console.error('Failed to load locations', error)
		}
		try {
			if (!hubStore.hubs.length) await hubStore.listAllHubs()
		} catch (error) {
			console.error('Failed to load hubs', error)
		}
		try {
			if (!comappStore.comapps.length) await comappStore.listComapps()
		} catch (error) {
			console.error('Failed to load comapps', error)
		}
	})
</script>

<template>
	<VModalFinal
		:modalId="modalId"
		:title="modalTitle"
		:closeOnBackgroudClick="false"
		:showButtons="false"
		:closeButton="true"
		size="xl">
		<div class="max-h-[75vh] min-h-[60vh] space-y-4 overflow-auto p-4">
			<div class="text-sm text-base-content/60">
				{{ _tr('location.assignHint') }}
			</div>

			<div class="grid gap-4 md:grid-cols-2">
				<div class="border-base-300 bg-base-200/50 rounded-lg border p-4">
					<div class="flex items-center justify-between gap-2">
						<h3 class="text-base font-semibold">{{ _tr('location.assignHub') }}</h3>
					</div>
					<div class="mt-3 flex flex-col gap-2">
						<v-select
							v-model="selectedHubId"
							class="w-full"
							:options="assignableHubs"
							:reduce="(opt) => opt.value"
							label="label"
							:clearable="true"
							:append-to-body="true"
							:placeholder="_tr('location.selectHub')">
							<template #option="{ label, locationLabel }">
								<div class="flex w-full items-center justify-between gap-2">
									<span class="truncate">{{ label }}</span>
									<span class="text-xs text-base-content/60">{{ locationLabel }}</span>
								</div>
							</template>
							<template #selected-option="{ label, locationLabel }">
								<div class="flex w-full items-center justify-between gap-2">
									<span class="truncate">{{ label }}</span>
									<span class="text-xs text-base-content/60">{{ locationLabel }}</span>
								</div>
							</template>
						</v-select>
						<button
							class="btn btn-primary"
							:disabled="!selectedHubId || isAssigningHub"
							@click="assignHub">
							<span v-if="isAssigningHub" class="loading loading-spinner loading-xs"></span>
							{{ _tr('location.assign') }}
						</button>
						<p v-if="!assignableHubs.length" class="text-sm text-base-content/60">
							{{ _tr('location.noAssignableHubs') }}
						</p>
					</div>
				</div>

				<div class="border-base-300 bg-base-200/50 rounded-lg border p-4">
					<div class="flex items-center justify-between gap-2">
						<h3 class="text-base font-semibold">{{ _tr('location.assignComapp') }}</h3>
					</div>
					<div class="mt-3 flex flex-col gap-2">
						<v-select
							v-model="selectedComappId"
							class="w-full"
							:options="assignableComapps"
							:reduce="(opt) => opt.value"
							label="label"
							:clearable="true"
							:append-to-body="true"
							:placeholder="_tr('location.selectComapp')">
							<template #option="{ label, locationLabel }">
								<div class="flex w-full items-center justify-between gap-2">
									<span class="truncate">{{ label }}</span>
									<span class="text-xs text-base-content/60">{{ locationLabel }}</span>
								</div>
							</template>
							<template #selected-option="{ label, locationLabel }">
								<div class="flex w-full items-center justify-between gap-2">
									<span class="truncate">{{ label }}</span>
									<span class="text-xs text-base-content/60">{{ locationLabel }}</span>
								</div>
							</template>
						</v-select>
						<button
							class="btn btn-primary"
							:disabled="!selectedComappId || isAssigningComapp"
							@click="assignComapp">
							<span v-if="isAssigningComapp" class="loading loading-spinner loading-xs"></span>
							{{ _tr('location.assign') }}
						</button>
						<p v-if="!assignableComapps.length" class="text-sm text-base-content/60">
							{{ _tr('location.noAssignableComapps') }}
						</p>
					</div>
				</div>
			</div>

			<div class="grid gap-4 md:grid-cols-2">
				<div class="border-base-300 bg-base-200/50 rounded-lg border p-4">
					<div class="flex items-center justify-between gap-2">
						<h3 class="text-base font-semibold">{{ _tr('location.assignedHubs') }}</h3>
						<span class="text-xs text-base-content/60">{{ assignedHubs.length }}</span>
					</div>
					<p v-if="!assignedHubs.length" class="mt-3 text-sm text-base-content/60">
						{{ _tr('location.noAssignedHubs') }}
					</p>
					<div v-else class="mt-3 space-y-2 max-h-64 overflow-auto pr-1">
						<div
							v-for="hub in assignedHubs"
							:key="hub._id"
							class="border-base-300/60 bg-base-100/70 flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
							<div class="min-w-0 flex items-center gap-2">
								<StatusLight type="hub" :_id="hub._id" />
								<div class="truncate font-medium">
									{{ hub.name || hub.serial || hub._id }}
								</div>
								<div v-if="hub.name && hub.serial" class="text-xs text-base-content/60">
									{{ hub.serial }}
								</div>
							</div>
							<ConfirmButton
								modal
								:title="_tr('location.unassign')"
								:text="_tr('location.unassignConfirm', { name: hub.name || hub.serial || hub._id })"
								@confirm="deassignHub(hub._id)">
								<button class="btn btn-sm btn-soft btn-error" :disabled="isAssigningHub">
									{{ _tr('location.unassign') }}
								</button>
							</ConfirmButton>
						</div>
					</div>
				</div>

				<div class="border-base-300 bg-base-200/50 rounded-lg border p-4">
					<div class="flex items-center justify-between gap-2">
						<h3 class="text-base font-semibold">{{ _tr('location.assignedComapps') }}</h3>
						<span class="text-xs text-base-content/60">{{ assignedComapps.length }}</span>
					</div>
					<p v-if="!assignedComapps.length" class="mt-3 text-sm text-base-content/60">
						{{ _tr('location.noAssignedComapps') }}
					</p>
					<div v-else class="mt-3 space-y-2 max-h-64 overflow-auto pr-1">
						<div
							v-for="comapp in assignedComapps"
							:key="comapp._id"
							class="border-base-300/60 bg-base-100/70 flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
							<div class="min-w-0 flex items-center gap-2">
								<StatusLight type="comapp" :_id="comapp._id" />
								<div class="truncate font-medium">
									{{ comapp.pcName || comapp._id }}
								</div>
							</div>
							<ConfirmButton
								modal
								:title="_tr('location.unassign')"
								:text="_tr('location.unassignConfirm', { name: comapp.pcName || comapp._id })"
								@confirm="deassignComapp(comapp._id)">
								<button class="btn btn-sm btn-soft btn-error" :disabled="isAssigningComapp">
									{{ _tr('location.unassign') }}
								</button>
							</ConfirmButton>
						</div>
					</div>
				</div>
			</div>
		</div>
	</VModalFinal>
</template>
